import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import CardContainer from '../../../components/card/card-container.jsx';
import Api from '../../../api/global.service';
import NoPagination from '../../../components/pagination/noPagination.jsx';
import env from '../../../environment.js';
import Swal from 'sweetalert2';
import SelectCiclo from '../../../components/select/select-ciclo.jsx';
import ListToolbar, { BtnNuevo } from '../../../nuup/componentes/Toolbar/ListToolbar.jsx';

const ReportesList = () => {
  const url = '/reportes';

  const [data, setData] = useState([]);
  const [fichaInfo, setFichaInfo] = useState(null);
  const [fichaLoading, setFichaLoading] = useState(false);
  const [fichaError, setFichaError] = useState(null);
  const [selectedCiclo, setSelectedCiclo] = useState(null);

  // Ref to store active stream controllers
  const activeStreams = useRef({});

  const fetchFicha = (idCiclo = null) => {
    setFichaLoading(true);
    setFichaError(null);

    const params = idCiclo ? { id_ciclo: idCiclo } : {};
    
    return Api.getAll('descargas-masivas/ultima-ficha', params)
      .then(response => {
        if (response.data && response.data.status === 'success') {
          console.log(params);
          console.log(response.data.data);
          setFichaInfo(response.data.data);
          return;
        }

        setFichaInfo(null);
        setFichaError('No se encontró una ficha para el ciclo seleccionado.');
      })
      .catch(e => {
        setFichaInfo(null);
        setFichaError('No se pudo consultar la ficha. Intente de nuevo o seleccione otro ciclo.');
        console.error('Error fetching ficha info', e);
      })
      .finally(() => {
        setFichaLoading(false);
      });
  };

  const fetchReports = (idKoboFichaPrincipal = null) => {
    if (!idKoboFichaPrincipal) {
      setData([]);
      return Promise.resolve();
    }
    console.log(idKoboFichaPrincipal);
    return Api.search('kobo-ficha-descargas', {
      filter: [
        { column: 'id_kobo_ficha_principal', operator: '=', value: idKoboFichaPrincipal },
      ],
    })
      .then(response => {
        setData(response.data);
      })
      .catch(e => {
        console.error(e);
      });
  };

  // Setup streams for pending/processing items
  useEffect(() => {
    if (!data || data.length === 0) return;

    const pendingItems = data.filter(item =>
      (item.status === 'pending' || item.status === 'processing') &&
      !activeStreams.current[item.id]
    );

    pendingItems.forEach(item => {
      connectStream(item.id);
    });

    // Cleanup function when component unmounts
    return () => {
      // We don't necessarily want to close streams on every data update, 
      // only on unmount. But here we are in the effect dependency [data].
      // If data updates, we might re-run. 
      // Ideally we only want to close streams that are no longer needed, 
      // but since we check !activeStreams.current[item.id], we won't duplicate.
      // The cleanup for unmount should be in a separate useEffect with [] dependency.
    };
  }, [data]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.values(activeStreams.current).forEach(controller => controller.abort());
      activeStreams.current = {};
    };
  }, []);

  const connectStream = async (id) => {
    if (activeStreams.current[id]) return;

    const controller = new AbortController();
    activeStreams.current[id] = controller;

    try {
      const token = localStorage.getItem(`${env.appName}-token`);
      const response = await fetch(`${env.apiURL}/kobo-ficha-descargas/${id}/stream`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
        },
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        const lines = buffer.split('\n');
        buffer = lines.pop(); // Keep the last partial line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const dataStr = line.trim().slice(6);
            if (dataStr === 'closed' || dataStr === 'timeout reached') {
              controller.abort();
              delete activeStreams.current[id];
              return;
            }
            try {
              const jsonData = JSON.parse(dataStr);

              // Update state
              setData(prevData => {
                return prevData.map(item =>
                  item.id === jsonData.id ? { ...item, ...jsonData } : item
                );
              });

              if (jsonData.status === 'completed' || jsonData.status === 'failed') {
                controller.abort();
                delete activeStreams.current[id];
                return;
              }
            } catch (e) {
              // console.warn('Failed to parse SSE data', e);
            }
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Stream error for ID ' + id, err);
        // Remove from active streams so it can be retried if needed (e.g. on manual refresh)
        delete activeStreams.current[id];
      }
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: '¿Está seguro?',
      text: "Esta acción eliminará el reporte permanentemente de la base de datos y del almacenamiento.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        // Eliminar stream activo si existe
        if (activeStreams.current[id]) {
          try {
            activeStreams.current[id].abort();
          } catch (e) { console.error(e); }
          delete activeStreams.current[id];
        }

        // Eliminación optimista: remover del estado inmediatamente
        setData(prevData => prevData.filter(item => item.id !== id));

        Api.delete('reportes/descarga-masiva', id)
          .then(response => {
            Swal.fire(
              'Eliminado',
              'El reporte ha sido eliminado correctamente.',
              'success'
            );
          })
          .catch(error => {
            // Revertir eliminación optimista en caso de error
            fetchReports(fichaInfo?.id_kobo_ficha_principal || null);
            
            Swal.fire(
              'Error',
              'No se pudo eliminar el reporte: ' + (error.response?.data?.error || error.message),
              'error'
            );
          });
      }
    });
  };

  const handleRegenerate = (id) => {
    Swal.fire({
      title: '¿Está seguro?',
      text: "Esto actualizará el archivo con los datos más recientes.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, regenerar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        // Limpiar stream activo si existe para forzar reconexión
        if (activeStreams.current[id]) {
          try {
            activeStreams.current[id].abort();
          } catch (e) { console.error(e); }
          delete activeStreams.current[id];
        }

        // Actualización optimista: marcar como processing inmediatamente
        setData(prevData => prevData.map(item =>
          item.id === id ? { ...item, status: 'processing', error_message: null } : item
        ));

        Api.create(`kobo-ficha-descargas/regenerate/${id}`, {})
          .then(response => {
            console.log('Regeneración iniciada para ID:', id);
          })
          .catch(error => {
            console.error('Error regenerating report:', error);
            // Revertir estado si falla
            fetchReports();
            Swal.fire(
              'Error',
              'Hubo un problema al intentar regenerar el reporte.',
              'error'
            );
          });
      }
    });
  };

  const handleDownload = (id, fileName) => {
    Api.download(`kobo-ficha-descargas/download/${id}`)
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName || `reporte-${id}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(error => {
        console.error('Error downloading file:', error);
        // alert('Error al descargar el archivo');
      });
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (!+bytes) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  };

  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return '-';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const columns = React.useMemo(
    () => [
      {
        Header: 'NOMBRE',
        accessor: 'nombre',
        sortable: true,
      },
      {
        Header: 'DESCRIPCIÓN',
        accessor: 'descripcion',
        sortable: true,
      },
      {
        Header: 'DURACIÓN / PESO',
        accessor: 'file_size',
        sortable: true,
        Cell: ({ value, row }) => `${formatTime(row.original.execution_time)} / ${formatBytes(row.original.file_size)}`,
      },
      {
        Header: 'FECHA DE CREACIÓN',
        accessor: 'created_at',
        sortable: true,
        Cell: ({ value }) => {
          if (!value) return '-';
          return value.split('T')[0];
        }
      },
      {
        Header: 'ESTADO',
        accessor: 'status',
        sortable: true,
        Cell: ({ value, row }) => {
          if (value === 'completed')
            return (
              <span
                style={{
                  backgroundColor: `${env.colors.primary_600}22`,
                  color: env.colors.primary_600,
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                }}>
                Generado
              </span>
            );
          if (value === 'pending')
            return (
              <span
                style={{
                  backgroundColor: `${env.colors.secondary_200}22`,
                  color: env.colors.secondary_500,
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                }}>
                Pendiente
              </span>
            );
          if (value === 'processing')
            return (
              <span
                style={{
                  backgroundColor: `${env.colors.secondary_600}22`,
                  color: env.colors.secondary_600,
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                }}>
                <i className="fa fa-spinner fa-spin mr-1"></i>
                {' '}
                Procesando
              </span>
            );
          if (value === 'failed') return <span className="badge badge-danger" title={row.original.error_message}>Fallido</span>;
          return value;
        }
      },
      {
        Header: 'ACCIONES',
        accessor: 'uuid', // Assuming uuid or id is available
        Cell: ({ row }) => {
          const status = row.original.status;
          const isBusy = status === 'processing';

          const canEdit = status === 'completed' || status === 'failed' || status === 'pending';
          const canRegenerate = status === 'completed' || status === 'failed';
          const canDownload = status === 'completed';
          const canDelete = status !== 'processing';

          const getDisabledStyle = disabled => ({
            opacity: disabled ? 0.55 : 1,
            cursor: disabled ? 'not-allowed' : 'pointer',
          });

          const editDisabled = !canEdit || isBusy;
          const regenerateDisabled = !canRegenerate || isBusy;
          const downloadDisabled = !canDownload || isBusy;
          const deleteDisabled = !canDelete || isBusy;

          const fileName = row.original.file_path
            ? row.original.file_path.split('/').pop()
            : (row.original.nombre ? `${row.original.nombre}.xlsx` : `reporte-${row.original.id}.xlsx`);

          return (
            <div>
              {!editDisabled ? (
                <Link to={{ pathname: `${url}/edit/${row.original.id}` }}>
                  <button className="btn btn-sm mr-2" title="Editar" style={getDisabledStyle(false)}>
                    <i className="fa fa-edit"></i>
                  </button>
                </Link>
              ) : (
                <button className="btn btn-sm mr-2" title="Editar" disabled style={getDisabledStyle(true)}>
                  <i className="fa fa-edit"></i>
                </button>
              )}

              <button
                className="btn btn-sm"
                title="Regenerar reporte"
                disabled={regenerateDisabled}
                style={getDisabledStyle(regenerateDisabled)}
                onClick={() => handleRegenerate(row.original.id)}>
                <i className="fa fa-redo"></i>
              </button>

              <button
                className="btn btn-sm mr-2"
                title="Descargar"
                disabled={downloadDisabled}
                style={getDisabledStyle(downloadDisabled)}
                onClick={() => handleDownload(row.original.id, fileName)}>
                <i className="fa fa-download"></i>
              </button>

              <button
                className="btn ml-2"
                title="Eliminar reporte"
                disabled={deleteDisabled}
                style={{ ...getDisabledStyle(deleteDisabled), borderRadius: 8 }}
                onClick={() => handleDelete(row.original.id)}>
                <i className="fa fa-trash"></i>
              </button>
            </div>
          );
        },
      },
    ],
    []
  );

  useEffect(() => {
    if(!selectedCiclo) return;
      fetchFicha(selectedCiclo.id);
  }, [selectedCiclo]);

  useEffect(() => {
    if (!selectedCiclo || !selectedCiclo.id) return;
    fetchFicha(selectedCiclo.id);
  }, [selectedCiclo]);

  useEffect(() => {
    fetchReports(fichaInfo?.id_kobo_ficha_principal || null);
  }, [fichaInfo?.id_kobo_ficha_principal]);



  return (
    <div>
      <SelectCiclo
        className="mr-3 mb-2 mb-md-0"
        width="188px"
        acopio={0}
        setFilter={({ target }) => {
          setSelectedCiclo(target || null);
        }}
      />
      <CardContainer height={'100%'}>

        <ListToolbar left={<h2 className="page-title mb-0">Reportes Personalizados</h2>}>
          <BtnNuevo
            to={{
              pathname: `${url}/create`,
              state: {
                id_ciclo: fichaInfo?.id_ciclo,
                id_kobo_ficha_principal: fichaInfo?.id_kobo_ficha_principal,
              },
            }}
            disabled={!fichaInfo || fichaLoading}
            onClick={e => {
              if (!fichaInfo) {
                e.preventDefault();
                Swal.fire('Sin ficha', 'No se encontró una ficha para el ciclo seleccionado. Seleccione otro ciclo.', 'warning');
              }
            }}>
            Nuevo reporte
          </BtnNuevo>
        </ListToolbar>

        {fichaLoading && (
          <div className="alert mb-4">
            <span className="text-muted"><i className="fa fa-spinner fa-spin"></i> Cargando ficha...</span>
          </div>
        )}

        {!fichaLoading && fichaError && (
          <div className="alert alert-warning mb-4">
            {fichaError}
          </div>
        )}

        <NoPagination columns={columns} data={data}></NoPagination>
      </CardContainer>
    </div>
  );
};

export default ReportesList;
